import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import Pagination from './Pagination';
import { getIPStatusTextClass, getIPStatusLabel } from '../utils/riskColors';
import { TIMEZONE_OPTIONS, getTzLabel, getTzOffset } from './TimelineView';
import CopyButton from './CopyButton';

// ── Helpers ──────────────────────────────────────────────────────────────────

function parseBrowser(userAgent) {
  if (!userAgent) return 'Unknown';
  const ua = userAgent.toLowerCase();
  if (ua === 'bav2ropc') return 'Legacy Auth';
  if (ua.includes('teams/')) return 'Teams';
  if (ua.includes('outlook-ios') || ua.includes('outlook-android')) return 'Outlook Mobile';
  if (ua.includes('microsoft office')) return 'Office Desktop';
  if (ua.includes('edg/') || ua.includes('edge/')) return 'Edge';
  if (ua.includes('chrome/') && !ua.includes('edg/')) return 'Chrome';
  if (ua.includes('firefox/')) return 'Firefox';
  if (ua.includes('safari/') && !ua.includes('chrome/')) return 'Safari';
  if (ua.includes('python') || ua.includes('powershell')) return 'Script/API';
  return 'Other';
}

function formatTs(ts, tzId) {
  if (!ts) return '\u2014';
  try {
    const d = new Date(ts);
    const tz = TIMEZONE_OPTIONS.find(t => t.id === tzId) || TIMEZONE_OPTIONS[0];
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz.iana,
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    return fmt.format(d);
  } catch {
    return ts;
  }
}

function complianceBorder(isCompliant) {
  if (isCompliant === true) return 'border-l-4 border-l-green-500';
  if (isCompliant === false) return 'border-l-4 border-l-amber-500';
  return 'border-l-4 border-l-gray-600';
}

function riskColorClass(score) {
  if (score == null) return 'text-gray-600';
  if (score >= 100) return 'text-red-400';
  if (score >= 30) return 'text-amber-400';
  if (score >= 0) return 'text-green-400';
  return 'text-blue-400';
}

function isWithinBaselineHours(timestamp, baselineHours) {
  if (!timestamp || !baselineHours) return false;
  try {
    const hour = new Date(timestamp).getUTCHours();
    return hour >= baselineHours.earliestHour && hour <= baselineHours.latestHour;
  } catch { return false; }
}

function deviceNameColorClass(ips, ipClassifications) {
  if (!ips || ips.length === 0) return 'text-gray-300';
  let hasMalicious = false, hasSuspect = false, allSafe = true;
  for (const ip of ips) {
    const status = ipClassifications[ip] || 'default';
    if (status === 'malicious') hasMalicious = true;
    if (status === 'suspect') hasSuspect = true;
    if (status !== 'safe') allSafe = false;
  }
  if (hasMalicious) return 'text-red-400';
  if (hasSuspect) return 'text-amber-400';
  if (allSafe) return 'text-green-400';
  return 'text-gray-300';
}

// Status indicator colors for the clickable circle (matches IPAddressTable)
const STATUS_CIRCLE_CLASSES = {
  safe: 'bg-green-500 border-green-400',
  suspect: 'bg-amber-500 border-amber-400',
  malicious: 'bg-red-500 border-red-400',
  default: 'bg-gray-600 border-gray-500',
};

// ── Columns ──────────────────────────────────────────────────────────────────

const COLUMNS = [
  { id: '_expand',          label: '',                 sortable: false, center: true  },
  { id: 'hostname',         label: 'Device Name',      sortable: true,  center: false },
  { id: 'os',               label: 'OS',               sortable: true,  center: false },
  { id: 'browsers',         label: 'Browser',          sortable: false, center: false },
  { id: 'isCompliant',      label: 'Intune Device Compliant', sortable: true,  center: true  },
  { id: 'drmmHostnameMatch',label: 'DRMM',             sortable: true,  center: true  },
  { id: 'signInCount',      label: 'Sign-Ins',         sortable: true,  center: true  },
  { id: 'successCount',     label: 'Success',           sortable: true,  center: true  },
  { id: 'failCount',        label: 'Failed',            sortable: true,  center: true  },
  { id: 'ips',              label: 'IPs',               sortable: true,  center: true  },
  { id: 'apps',             label: 'M365 Apps',         sortable: true,  center: true  },
  { id: 'firstSeen',        label: 'First Seen',        sortable: true,  center: false },
  { id: 'lastSeen',         label: 'Last Seen',         sortable: true,  center: false },
  { id: 'riskMax',          label: 'Max Risk',          sortable: true,  center: true  },
];

const COL_WIDTHS_INIT = [32, 200, 120, 140, 140, 70, 70, 70, 70, 50, 80, 140, 140, 80];

// ── Component ────────────────────────────────────────────────────────────────

export default function DevicesTab({
  signInEvents = [],
  ipClassifications = {},
  ipComments = {},
  selectedTz = 'ET',
  onCycleIPStatus,
  userBaseline = null,
  nonInteractive = null,
  caseStartDate = '',
  caseEndDate = '',
}) {
  const [filterQuery, setFilterQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('signInCount');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expandedRow, setExpandedRow] = useState(null);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [colWidths, setColWidths] = useState(COL_WIDTHS_INIT);

  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(32, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };

    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  // ── Baseline OS lookup ──

  const baselineOSMap = useMemo(() => {
    if (!userBaseline?.osProfiles?.length) return {};
    const map = {};
    userBaseline.osProfiles.forEach(p => {
      map[p.os] = { earliestHour: p.earliestHour, latestHour: p.latestHour, signinCount: p.signinCount };
    });
    return map;
  }, [userBaseline]);

  // ── Aggregate sign-in events by device ──

  const devices = useMemo(() => {
    const map = {};

    signInEvents.forEach((evt) => {
      const hostname = evt.DeviceHostname || '';
      const os = evt.NormalizedOS || evt.DeviceOS || 'Unknown';
      const browser = parseBrowser(evt.UserAgent);

      // Named devices: group by hostname+OS. Unnamed: by OS+browser.
      const key = hostname
        ? `${hostname}|${os}`
        : `(unnamed)|${os}|${browser}`;

      if (!map[key]) {
        map[key] = {
          key,
          hostname: hostname || null,
          os,
          rawOS: evt.DeviceOS || 'Unknown',
          browsers: new Set(),
          ips: new Set(),
          apps: new Set(),
          sessions: new Set(),
          isCompliant: null,
          drmmHostnameMatch: null,
          signInCount: 0,
          successCount: 0,
          failCount: 0,
          firstSeen: evt.EventTimeUTC,
          lastSeen: evt.EventTimeUTC,
          riskMin: null,
          riskMax: null,
          events: [],
        };
      }

      const d = map[key];
      d.browsers.add(browser);
      if (evt.IPAddress) d.ips.add(evt.IPAddress);
      if (evt.ApplicationName || evt.AppDisplayName) d.apps.add(evt.ApplicationName || evt.AppDisplayName);
      if (evt.SessionId) d.sessions.add(evt.SessionId);

      // Compliance: true wins over false wins over null
      if (evt.IsDeviceCompliant === true) d.isCompliant = true;
      else if (d.isCompliant === null && evt.IsDeviceCompliant === false) d.isCompliant = false;

      // DRMM
      if (evt.DRMM_HostnameMatched === true) d.drmmHostnameMatch = true;
      else if (d.drmmHostnameMatch === null && evt.DRMM_HostnameMatched === false) d.drmmHostnameMatch = false;

      // Timestamps
      if (evt.EventTimeUTC && evt.EventTimeUTC < d.firstSeen) d.firstSeen = evt.EventTimeUTC;
      if (evt.EventTimeUTC && evt.EventTimeUTC > d.lastSeen) d.lastSeen = evt.EventTimeUTC;

      // Counts
      d.signInCount++;
      const status = (evt.ResultStatus || '').toLowerCase();
      const op = (evt.Operation || '').toLowerCase();
      if (status === 'failed' || status === 'failure' || op.includes('failed')) {
        d.failCount++;
      } else if (status === 'success' || status === 'succeeded' || op.includes('loggedin')) {
        d.successCount++;
      }

      // Risk range
      const risk = evt.RiskScore ?? evt.Risk_Total;
      if (risk != null) {
        if (d.riskMin === null || risk < d.riskMin) d.riskMin = risk;
        if (d.riskMax === null || risk > d.riskMax) d.riskMax = risk;
      }

      d.events.push(evt);
    });

    return Object.values(map).map(d => ({
      ...d,
      browsers: [...d.browsers],
      ips: [...d.ips],
      apps: [...d.apps],
      sessions: [...d.sessions],
      isBaselineOS: !!baselineOSMap[d.os],
      baselineHours: baselineOSMap[d.os] || null,
    }));
  }, [signInEvents, baselineOSMap]);

  // ── Summary stats ──

  const stats = useMemo(() => {
    let compliant = 0, nonCompliant = 0, unknown = 0, drmmMatched = 0;
    devices.forEach(d => {
      if (d.isCompliant === true) compliant++;
      else if (d.isCompliant === false) nonCompliant++;
      else unknown++;
      if (d.drmmHostnameMatch === true) drmmMatched++;
    });
    return { total: devices.length, compliant, nonCompliant, unknown, drmmMatched };
  }, [devices]);

  // ── Filter ──

  const filteredDevices = useMemo(() => {
    if (!filterQuery.trim()) return devices;
    const q = filterQuery.toLowerCase();
    return devices.filter(d =>
      (d.hostname && d.hostname.toLowerCase().includes(q)) ||
      d.os.toLowerCase().includes(q) ||
      d.rawOS.toLowerCase().includes(q) ||
      d.browsers.some(b => b.toLowerCase().includes(q)) ||
      d.ips.some(ip => ip.toLowerCase().includes(q)) ||
      d.apps.some(a => a.toLowerCase().includes(q))
    );
  }, [devices, filterQuery]);

  // ── Sort ──

  const sortedDevices = useMemo(() => {
    const sorted = [...filteredDevices];
    sorted.sort((a, b) => {
      let va, vb;
      switch (sortColumn) {
        case 'hostname':
          va = (a.hostname || '').toLowerCase();
          vb = (b.hostname || '').toLowerCase();
          break;
        case 'os':
          va = a.os.toLowerCase();
          vb = b.os.toLowerCase();
          break;
        case 'isCompliant':
          va = a.isCompliant === true ? 2 : a.isCompliant === false ? 1 : 0;
          vb = b.isCompliant === true ? 2 : b.isCompliant === false ? 1 : 0;
          break;
        case 'drmmHostnameMatch':
          va = a.drmmHostnameMatch === true ? 2 : a.drmmHostnameMatch === false ? 1 : 0;
          vb = b.drmmHostnameMatch === true ? 2 : b.drmmHostnameMatch === false ? 1 : 0;
          break;
        case 'ips':
          va = a.ips.length; vb = b.ips.length; break;
        case 'apps':
          va = a.apps.length; vb = b.apps.length; break;
        case 'firstSeen':
          va = a.firstSeen || ''; vb = b.firstSeen || ''; break;
        case 'lastSeen':
          va = a.lastSeen || ''; vb = b.lastSeen || ''; break;
        case 'riskMax':
          va = a.riskMax ?? -999; vb = b.riskMax ?? -999; break;
        default:
          va = a[sortColumn] ?? 0; vb = b[sortColumn] ?? 0;
      }
      if (va < vb) return sortDirection === 'asc' ? -1 : 1;
      if (va > vb) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredDevices, sortColumn, sortDirection]);

  // ── Pagination ──

  const totalPages = Math.max(1, Math.ceil(sortedDevices.length / pageSize));
  const pagedDevices = sortedDevices.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, sortColumn, sortDirection]);
  useEffect(() => { setExpandedRow(null); setShowAllEvents(false); }, [currentPage, pageSize]);

  const handleSort = (colId) => {
    if (sortColumn === colId) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(colId);
      setSortDirection('desc');
    }
  };

  // ── Empty state ──

  if (signInEvents.length === 0) {
    return (
      <div className="card text-center py-16">
        <ComputerDesktopIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">No Device Data</h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          Device information will appear here after running an investigation.
          Only sign-in events contain device properties.
        </p>
      </div>
    );
  }

  const tzLabel = getTzLabel(selectedTz);
  const tzInfo = TIMEZONE_OPTIONS.find(t => t.id === selectedTz) || TIMEZONE_OPTIONS[0];
  const tzOffsetHours = parseFloat(getTzOffset(tzInfo.iana)) || 0;
  const convertHour = (utcHour) => Math.floor(((utcHour + tzOffsetHours) % 24 + 24) % 24);
  const minTableWidth = colWidths.reduce((s, w) => s + w, 0);

  // Devices seen ONLY in the non-interactive feed.
  //
  // This tab is built entirely from `signInEvents[].DeviceHostname`, so a device that only
  // ever presented an already-issued token is structurally absent from it -- there is no
  // interactive sign-in to carry the name. On the reference case that is `DESKTOP-D61DA9`,
  // which is invisible in EVERY audit-driven and sign-in-driven view: it has zero rows in
  // the shared events table, and no `Add device` audit event either, because directory_audit
  // coverage for that tenant begins eleven days after the device was registered.
  //
  // Rendered as a SEPARATE section rather than merged into the table above, because these
  // rows are not the same KIND of fact: the table's devices have sign-ins, sessions, IPs,
  // hours and compliance state, and a merged row would be mostly empty cells that read as
  // "we checked and found nothing" rather than "this comes from a different feed".
  const caseDeviceNameSet = useMemo(() => {
    const set = new Set();
    // `devices` above keys the name as `hostname`. Read both, so a later rename of either
    // side degrades to showing an extra row rather than to a false claim about every row.
    for (const d of devices) {
      const n = d.hostname || d.name;
      if (n) set.add(String(n).trim().toUpperCase());
    }
    return set;
  }, [devices]);

  const nonInteractiveOnlyDevices = useMemo(() => {
    const out = [];
    // A device registered DURING the case window is the attacker-characteristic shape -
    // on the reference case every attacker device was registered inside it (07-09 to
    // 08-03) while the victim's own BYOD dates from 2024 and January. It is a strong
    // discriminator and it is free, so it is used to RANK and LABEL - never to hide.
    // Suppressing the rest is how this feature got into trouble twice already: a list the
    // analyst cannot see is one they cannot correct.
    const winStart = caseStartDate ? Date.parse(`${caseStartDate}T00:00:00Z`) : NaN;
    const winEnd = caseEndDate ? Date.parse(`${caseEndDate}T23:59:59Z`) : NaN;

    for (const d of (nonInteractive?.devices || [])) {
      const name = d?.name || d?.displayName;
      if (!name) continue;
      if (caseDeviceNameSet.has(String(name).toUpperCase())) continue;
      // Prefer IPs derived from the IP rows; fall back to the endpoint's own device
      // roll-up, which carries them when the per-IP device list was capped.
      const derived = (nonInteractive?.ips || [])
        .filter((r) => (r.devices || []).some((x) => String(x).toUpperCase() === String(name).toUpperCase()))
        .map((r) => r.ip);
      const ips = derived.length ? derived : (d.ips || []);

      const reg = d.registeredUtc ? Date.parse(d.registeredUtc) : NaN;
      const registeredInWindow = Number.isFinite(reg) && Number.isFinite(winStart)
        && Number.isFinite(winEnd) && reg >= winStart && reg <= winEnd;

      out.push({ ...d, name, ips, registeredInWindow });
    }

    // Shared OS build across two or more IN-WINDOW devices: a set-level property a real
    // fleet does not have (the build is baked in at registration). Only computed over
    // in-window devices, so a corporate estate on one image is not flagged.
    const buildCount = {};
    for (const d of out) {
      if (d.registeredInWindow && d.osVersion) {
        buildCount[d.osVersion] = (buildCount[d.osVersion] || 0) + 1;
      }
    }
    for (const d of out) {
      d.sharedBuild = !!(d.registeredInWindow && d.osVersion && buildCount[d.osVersion] > 1);
    }

    return out.sort((a, b) => {
      if (a.registeredInWindow !== b.registeredInWindow) return a.registeredInWindow ? -1 : 1;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [nonInteractive, caseDeviceNameSet, caseStartDate, caseEndDate]);

  return (
    <div>
      {nonInteractiveOnlyDevices.length > 0 && (
        <div className="mb-6 rounded-lg border border-sky-900/40 bg-sky-950/10 p-3">
          <div className="flex items-center gap-2 mb-1">
            <ComputerDesktopIcon className="h-4 w-4 text-sky-400" />
            <span className="text-xs font-semibold text-sky-300">
              Not in this case&apos;s device list ({nonInteractiveOnlyDevices.length})
            </span>
          </div>
          <p className="text-[11px] text-gray-500 mb-2">
            These devices appear in the non-interactive sign-in feed but have no rows in the table
            below, so there are no sessions, hours or compliance state to show for them here. They are
            not included in the counts above. Most often that means the device only ever presented an
            already-issued token; it can also mean its interactive sign-ins fall outside this
            case&apos;s window.
          </p>
          <p className="text-[11px] text-gray-600 mb-2">
            How to weigh these is explained under{' '}
            <span className="text-gray-500">When and how to evaluate non-interactive sign-ins</span> on
            the IP Addresses tab.
          </p>
          <div className="space-y-1.5">
            {nonInteractiveOnlyDevices.map((d) => (
              <div key={d.name} className="text-xs flex flex-wrap items-center gap-x-3 gap-y-0.5">
                <span className={d.registeredInWindow ? 'font-medium text-amber-200' : 'font-medium text-gray-300'}>
                  {d.name}
                </span>
                {d.registeredInWindow && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-amber-950/40 text-amber-300 border border-amber-900/50"
                    title="This device was registered inside the case window — it did not exist before the period under investigation."
                  >
                    registered during this case
                  </span>
                )}
                {d.sharedBuild && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-red-950/40 text-red-300 border border-red-900/50"
                    title="Shares an identical OS build with another device registered in this window. The build is fixed at registration, so a shared value across devices enrolled days apart is a set-level property a real fleet does not have."
                  >
                    shared build
                  </span>
                )}
                {d.trustType && <span className="text-gray-500">{d.trustType}</span>}
                {d.registeredUtc && (
                  <span className={d.registeredInWindow ? 'text-amber-300/80' : 'text-gray-500'}>
                    registered {String(d.registeredUtc).slice(0, 10)}
                  </span>
                )}
                {d.osVersion && <span className="text-gray-500">OS {d.osVersion}</span>}
                {d.ips.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-gray-500">
                    on
                    {d.ips.map((ip, i) => (
                      <span key={ip} className="inline-flex items-center gap-1">
                        {i > 0 && <span className="text-gray-600">,</span>}
                        <span className={getIPStatusTextClass(ipClassifications[ip] || 'default')}>{ip}</span>
                      </span>
                    ))}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-3">
          <ComputerDesktopIcon className="h-8 w-8 text-blue-400 flex-shrink-0" />
          <div>
            <p className="text-2xl font-bold text-gray-100">{stats.total}</p>
            <p className="text-xs text-gray-500">Total Devices</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <CheckCircleIcon className="h-8 w-8 text-green-400 flex-shrink-0" />
          <div>
            <p className="text-2xl font-bold text-green-400">{stats.compliant}</p>
            <p className="text-xs text-gray-500">Intune Compliant Devices</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <ExclamationTriangleIcon className="h-8 w-8 text-amber-400 flex-shrink-0" />
          <div>
            <p className="text-2xl font-bold text-amber-400">{stats.nonCompliant}</p>
            <p className="text-xs text-gray-500">Non-Compliant (Intune)</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <QuestionMarkCircleIcon className="h-8 w-8 text-gray-500 flex-shrink-0" />
          <div>
            <p className="text-2xl font-bold text-gray-400">{stats.unknown}</p>
            <p className="text-xs text-gray-500">Unknown</p>
          </div>
        </div>
      </div>

      {stats.drmmMatched > 0 && (
        <div className="flex items-center gap-2 mb-4 text-xs text-gray-500">
          <ShieldCheckIcon className="h-4 w-4 text-green-500" />
          <span>{stats.drmmMatched} device{stats.drmmMatched !== 1 ? 's' : ''} matched in DRMM inventory</span>
        </div>
      )}

      {/* ── Search Bar ── */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search devices..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <span className="text-xs text-gray-500">
          {filteredDevices.length} of {devices.length} device{devices.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Table ── */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: minTableWidth + 'px' }}
          >
            <thead>
              <tr className="border-b border-gray-700 bg-gray-800/50">
                {COLUMNS.map((col, i) => (
                  <th
                    key={col.id}
                    ref={el => { thRefs.current[i] = el; }}
                    style={{ width: colWidths[i] + 'px' }}
                    onClick={col.sortable ? () => handleSort(col.id) : undefined}
                    className={`relative px-3 py-2.5 text-xs font-medium text-gray-500 overflow-hidden ${col.center ? 'text-center' : 'text-left'} ${col.sortable ? 'cursor-pointer hover:text-gray-300 select-none whitespace-nowrap' : ''}`}
                  >
                    <span className={`flex items-center${col.center ? ' justify-center' : ''}`}>
                      {col.label}
                      {col.sortable && sortColumn === col.id && (
                        <span className="ml-1 text-blue-400">{sortDirection === 'asc' ? '\u25B2' : '\u25BC'}</span>
                      )}
                    </span>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, i)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedDevices.map((device, idx) => {
                const isExpanded = expandedRow === idx;
                return (
                  <React.Fragment key={device.key}>
                    {/* ── Device Row ── */}
                    <tr
                      className={`hover:bg-gray-800/30 cursor-pointer ${complianceBorder(device.isCompliant)}`}
                      onClick={() => { setExpandedRow(isExpanded ? null : idx); setShowAllEvents(false); }}
                    >
                      {/* Expand chevron */}
                      <td className="px-3 py-2 text-center">
                        <ChevronRightIcon className={`h-4 w-4 text-gray-600 transition-transform inline-block ${isExpanded ? 'rotate-90' : ''}`} />
                      </td>
                      {/* Device Name */}
                      <td className="px-3 py-2">
                        {device.hostname ? (
                          <span className={`font-mono text-sm ${deviceNameColorClass(device.ips, ipClassifications)}`}>
                            {device.hostname}
                          </span>
                        ) : (
                          <span className="text-gray-600 italic text-sm">(unnamed device)</span>
                        )}
                      </td>
                      {/* OS */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 ${device.isBaselineOS ? 'text-green-400' : 'text-gray-400'}`}
                          title={device.isBaselineOS ? `Baseline OS — typical hours: ${convertHour(device.baselineHours?.earliestHour ?? 0)}:00–${convertHour(device.baselineHours?.latestHour ?? 23)}:00 ${tzLabel} (${device.baselineHours?.signinCount ?? 0} sign-ins in window)` : undefined}
                        >
                          {(device.os === 'iOS' || device.os === 'Android')
                            ? <DevicePhoneMobileIcon className="h-3.5 w-3.5 inline" />
                            : <ComputerDesktopIcon className="h-3.5 w-3.5 inline" />
                          }
                          {device.rawOS}
                        </span>
                      </td>
                      {/* Browser */}
                      <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                        {device.browsers.join(', ')}
                      </td>
                      {/* Compliant */}
                      <td className="px-3 py-2 text-center">
                        {device.isCompliant === true ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-900/60 text-green-300 border border-green-700/50">Yes</span>
                        ) : device.isCompliant === false ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-900/60 text-amber-300 border border-amber-700/50">No</span>
                        ) : (
                          <span className="text-gray-600">--</span>
                        )}
                      </td>
                      {/* DRMM */}
                      <td className="px-3 py-2 text-center">
                        {device.drmmHostnameMatch === true ? (
                          <span className="text-green-400 font-medium text-xs">Yes</span>
                        ) : device.drmmHostnameMatch === false ? (
                          <span className="text-gray-600 text-xs">No</span>
                        ) : (
                          <span className="text-gray-600">--</span>
                        )}
                      </td>
                      {/* Sign-Ins */}
                      <td className="px-3 py-2 text-center text-gray-300 font-mono">{device.signInCount}</td>
                      {/* Success */}
                      <td className="px-3 py-2 text-center text-green-400 font-mono">{device.successCount}</td>
                      {/* Failed */}
                      <td className="px-3 py-2 text-center font-mono">
                        {device.failCount > 0
                          ? <span className="text-red-400">{device.failCount}</span>
                          : <span className="text-gray-600">0</span>
                        }
                      </td>
                      {/* IPs */}
                      <td className="px-3 py-2 text-center text-gray-400 font-mono">{device.ips.length}</td>
                      {/* Apps */}
                      <td className="px-3 py-2 text-center text-gray-400 font-mono">{device.apps.length}</td>
                      {/* First Seen */}
                      <td className={`px-3 py-2 font-mono text-xs whitespace-nowrap ${device.baselineHours && isWithinBaselineHours(device.firstSeen, device.baselineHours) ? 'text-green-400' : 'text-gray-400'}`}>
                        {formatTs(device.firstSeen, selectedTz)}
                      </td>
                      {/* Last Seen */}
                      <td className={`px-3 py-2 font-mono text-xs whitespace-nowrap ${device.baselineHours && isWithinBaselineHours(device.lastSeen, device.baselineHours) ? 'text-green-400' : 'text-gray-400'}`}>
                        {formatTs(device.lastSeen, selectedTz)}
                      </td>
                      {/* Max Risk */}
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        {device.riskMax != null ? (
                          <span className={`font-mono text-xs ${riskColorClass(device.riskMax)}`}>
                            {device.riskMin === device.riskMax ? device.riskMax : `${device.riskMin}\u2013${device.riskMax}`}
                          </span>
                        ) : (
                          <span className="text-gray-600">--</span>
                        )}
                      </td>
                    </tr>

                    {/* ── Expanded Detail ── */}
                    {isExpanded && (
                      <tr className="bg-gray-800/30">
                        <td colSpan={COLUMNS.length} className="px-6 py-4">
                          <ExpandedDeviceDetail
                            device={device}
                            ipClassifications={ipClassifications}
                            ipComments={ipComments}
                            selectedTz={selectedTz}
                            tzLabel={tzLabel}
                            showAll={showAllEvents}
                            onToggleShowAll={() => setShowAllEvents(prev => !prev)}
                            onCycleIPStatus={onCycleIPStatus}
                            baselineHours={device.baselineHours}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {pagedDevices.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-gray-600">
                    No devices match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={sortedDevices.length}
          onPageChange={(p) => setCurrentPage(p)}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
        />
      </div>
    </div>
  );
}

// ── Expanded Detail Sub-Component ────────────────────────────────────────────

const EVENT_LIMIT = 50;

function ExpandedDeviceDetail({ device, ipClassifications, ipComments = {}, selectedTz, tzLabel, showAll, onToggleShowAll, onCycleIPStatus, baselineHours }) {
  const sortedEvents = useMemo(() =>
    [...device.events].sort((a, b) => (b.EventTimeUTC || '').localeCompare(a.EventTimeUTC || '')),
    [device.events]
  );

  const displayEvents = showAll ? sortedEvents : sortedEvents.slice(0, EVENT_LIMIT);
  const hasMore = sortedEvents.length > EVENT_LIMIT;

  return (
    <div>
      {/* Device summary line */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 text-xs text-gray-500">
        <span>Sessions: <span className="text-gray-300">{device.sessions.length}</span></span>
        <span className="inline-flex items-center gap-1">IPs:
          {device.ips.length > 0 ? device.ips.map((ip, i) => {
            const ipStatus = ipClassifications[ip] || 'default';
            return (
              <span key={ip} className="inline-flex items-center gap-1">
                {i > 0 && <span className="text-gray-600">,</span>}
                <span className={getIPStatusTextClass(ipStatus)}>{ip}</span>
                {ipComments[ip] && <span className="text-gray-500 text-[10px]" title={ipComments[ip]}>{ipComments[ip]}</span>}
                <CopyButton text={ip} className="opacity-40 hover:opacity-100" />
                {onCycleIPStatus && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onCycleIPStatus(ip); }}
                    className="p-0.5 rounded transition-colors group"
                    title={`${getIPStatusLabel(ipStatus) || 'Default'} — click to cycle`}
                  >
                    <span className={`inline-block h-3 w-3 rounded-full border-2 transition-colors ${STATUS_CIRCLE_CLASSES[ipStatus] || STATUS_CIRCLE_CLASSES.default} group-hover:opacity-80`} />
                  </button>
                )}
              </span>
            );
          }) : <span className="text-gray-300">{'\u2014'}</span>}
        </span>
        <span>Apps: <span className="text-gray-300">{device.apps.join(', ') || '\u2014'}</span></span>
      </div>

      {/* Event sub-table */}
      <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-700 text-gray-500">
              <th className="px-3 py-2 text-left font-medium">Time ({tzLabel})</th>
              <th className="px-3 py-2 text-left font-medium">IP Address</th>
              <th className="px-3 py-2 text-left font-medium">Comment</th>
              <th className="px-3 py-2 text-left font-medium">Application</th>
              <th className="px-3 py-2 text-left font-medium">Browser</th>
              <th className="px-3 py-2 text-center font-medium">Status</th>
              <th className="px-3 py-2 text-center font-medium">Risk</th>
              <th className="px-3 py-2 text-left font-medium">Session ID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/50">
            {displayEvents.map((evt, i) => {
              const ipStatus = ipClassifications[evt.IPAddress] || 'default';
              const ipClass = getIPStatusTextClass(ipStatus);
              const status = (evt.ResultStatus || '').toLowerCase();
              const op = (evt.Operation || '').toLowerCase();
              const isFailed = status === 'failed' || status === 'failure' || op.includes('failed');
              const risk = evt.RiskScore ?? evt.Risk_Total;

              return (
                <tr key={evt.EventId || i} className="hover:bg-gray-800/30">
                  <td className={`px-3 py-1.5 font-mono whitespace-nowrap ${baselineHours && isWithinBaselineHours(evt.EventTimeUTC, baselineHours) ? 'text-green-400' : 'text-gray-400'}`}>
                    {formatTs(evt.EventTimeUTC, selectedTz)}
                  </td>
                  <td className={`px-3 py-1.5 font-mono whitespace-nowrap ${ipClass} group`}>
                    <span className="inline-flex items-center gap-1">
                      {evt.IPAddress || '\u2014'}
                      {evt.IPAddress && <CopyButton text={evt.IPAddress} />}
                      {evt.IPAddress && onCycleIPStatus && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onCycleIPStatus(evt.IPAddress); }}
                          className="p-0.5 rounded transition-colors group"
                          title={`${getIPStatusLabel(ipStatus) || 'Default'} — click to cycle`}
                        >
                          <span className={`inline-block h-3 w-3 rounded-full border-2 transition-colors ${STATUS_CIRCLE_CLASSES[ipStatus] || STATUS_CIRCLE_CLASSES.default} group-hover:opacity-80`} />
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-gray-500 max-w-[180px] truncate" title={ipComments[evt.IPAddress] || undefined}>
                    {ipComments[evt.IPAddress] || '\u2014'}
                  </td>
                  <td className="px-3 py-1.5 text-gray-400 max-w-[200px] truncate" title={evt.ApplicationName || evt.AppDisplayName}>
                    {evt.ApplicationName || evt.AppDisplayName || '\u2014'}
                  </td>
                  <td className="px-3 py-1.5 text-gray-400 whitespace-nowrap">
                    {parseBrowser(evt.UserAgent)}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {isFailed
                      ? <span className="text-red-400">Failed</span>
                      : <span className="text-green-400">Success</span>
                    }
                  </td>
                  <td className={`px-3 py-1.5 text-center font-mono ${riskColorClass(risk)}`}>
                    {risk != null ? risk : '\u2014'}
                  </td>
                  <td className="px-3 py-1.5 text-gray-600 font-mono truncate max-w-[120px]" title={evt.SessionId}>
                    {evt.SessionId ? evt.SessionId.slice(0, 8) + '\u2026' : '\u2014'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button
          onClick={onToggleShowAll}
          className="mt-2 text-xs text-blue-400 hover:text-blue-300"
        >
          {showAll ? `Show first ${EVENT_LIMIT}` : `Show all ${sortedEvents.length} events`}
        </button>
      )}
    </div>
  );
}
